import React, { useEffect, useState, useRef } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import RightArrow from "../../components/SVG";
import { createJobApplication } from "../../services/jobsServices";
import toast from "react-hot-toast";
import FileUpload from "../../components/admin/common/FileUpload";
import { X } from "lucide-react";

const JobApplyMain = () => {
  const presentationRef = useRef();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get("applicationId");
  const [presentations, setPresentations] = useState([]);
  const fileInputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { state } = useLocation(); // 👈 get state passed from Link
  const jobData = state || "";

  const [formData, setFormData] = useState({
    fullName: "",
    birthDate: "",
    gender: "",
    city: "",
    phone: "",
    email: "",
    highestQualification: "",
    yearOfGraduation: "",
    jobTitle: "",
    experience: "",
    declarationConfirmed: false,
    jobId: applicationId,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const currentYear = new Date().getFullYear();

    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.birthDate) newErrors.birthDate = "Birth date is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.highestQualification)
      newErrors.highestQualification = "Highest qualification is required";
    if (!formData.yearOfGraduation) {
      newErrors.yearOfGraduation = "Year of graduation is required";
    } else if (
      isNaN(formData.yearOfGraduation) ||
      formData.yearOfGraduation < 1950 ||
      formData.yearOfGraduation > currentYear
    ) {
      newErrors.yearOfGraduation = `Please enter a year between 1950 and ${currentYear}`;
    }
    if (!formData.declarationConfirmed)
      newErrors.declarationConfirmed = "You must confirm the declaration";

    if (!formData.jobTitle) newErrors.jobTitle = "JobTitle is required";
    if (!formData.experience) newErrors.experience = "Experience is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setIsSubmitting(true);

    try {
      const applicationData = {
        ...formData,
        resumeUrl: presentations[0]?.url || "",
      };

      await createJobApplication(applicationData);
      toast.success("Application submitted successfully!");
      setFormData({
        fullName: "",
        birthDate: "",
        gender: "",
        city: "",
        phone: "",
        email: "",
        highestQualification: "",
        yearOfGraduation: "",
        jobTitle: "",
        experience: "",
        declarationConfirmed: false,
        jobId: applicationId,
      });
      setPresentations([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      toast.error(error.message || "Failed to submit application");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePresentation = (index) => {
    setPresentations((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <main>
      <Breadcrumb
        title="Application"
        subtitle={{ label: "Jobs", link: "/jobs" }}
      />

      <div className="it-student-area it-instructor-style">
        <div className="container">
          <div className="row">
            <div className="col-xl-12">
              <div className="it-student-bg">
                <form onSubmit={handleSubmit}>
                  <div className="col-xxl-12 mb-4">
                    <div className="postbox__details-wrapper">
                      <article>
                        {jobData ? (
                          <div
                            className="event-description-content mb-0 pb-10"
                            dangerouslySetInnerHTML={{
                              __html: jobData,
                            }}
                          />
                        ) : (
                          <p>No description available for this job.</p>
                        )}
                      </article>
                    </div>
                  </div>
                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">
                      Profile information
                    </h4>
                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Full Name *</label>
                            <input
                              type="text"
                              name="fullName"
                              value={formData.fullName}
                              onChange={handleChange}
                            />
                            {errors.fullName && (
                              <div className="error-message">
                                {errors.fullName}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Birth Date *</label>
                            <input
                              type="date"
                              name="birthDate"
                              value={formData.birthDate}
                              onChange={handleChange}
                            />
                            {errors.birthDate && (
                              <div className="error-message">
                                {errors.birthDate}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Gender *</label>
                            <div className="postbox__select">
                              <select
                                name="gender"
                                value={formData.gender}
                                onChange={handleChange}
                                className="w-full p-2 h-[55px] border border-black rounded focus:outline-none"
                              >
                                <option value="">Select a Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                              </select>
                              {errors.gender && (
                                <div className="error-message">
                                  {errors.gender}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>City *</label>
                            <input
                              type="text"
                              name="city"
                              value={formData.city}
                              onChange={handleChange}
                            />
                            {errors.city && (
                              <div className="error-message">{errors.city}</div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Phone *</label>
                            <input
                              type="text"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                            />
                            {errors.phone && (
                              <div className="error-message">
                                {errors.phone}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Email Id *</label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                            />
                            {errors.email && (
                              <div className="error-message">
                                {errors.email}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">
                      Education Background
                    </h4>

                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Highest Qualification *</label>
                            <div className="postbox__select">
                              <select
                                name="highestQualification"
                                className="w-full p-2 h-[55px] border border-black rounded focus:outline-none"
                                value={formData.highestQualification}
                                onChange={handleChange}
                              >
                                <option value="">
                                  Select Highest Qualification
                                </option>
                                <option value="High School">High School</option>
                                <option value="Bachelor's Degree">
                                  Bachelor's Degree
                                </option>
                                <option value="Master's Degree">
                                  Master's Degree
                                </option>
                                <option value="PhD">PhD</option>
                                <option value="Other">Other</option>
                              </select>
                              {errors.highestQualification && (
                                <div className="error-message">
                                  {errors.highestQualification}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Year of Graduation*</label>
                            <input
                              type="number"
                              name="yearOfGraduation"
                              value={formData.yearOfGraduation}
                              onChange={handleChange}
                              min="1950"
                              max={new Date().getFullYear()}
                            />
                            {errors.yearOfGraduation && (
                              <div className="error-message">
                                {errors.yearOfGraduation}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="it-student-regiform">
                    <h4 className="it-student-regiform-title">
                      Work Experience
                    </h4>

                    <div className="it-student-regiform-wrap">
                      <div className="row">
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Job Title</label>
                            <input
                              type="text"
                              name="jobTitle"
                              value={formData.jobTitle}
                              onChange={handleChange}
                            />
                            {errors.jobTitle && (
                              <div className="error-message">
                                {errors.jobTitle}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <div className="it-student-regiform-item">
                            <label>Experience</label>
                            <input
                              type="text"
                              name="experience"
                              value={formData.experience}
                              onChange={handleChange}
                            />
                            {errors.experience && (
                              <div className="error-message">
                                {errors.experience}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="col-xl-12 mb-4">
                        <FileUpload
                          ref={presentationRef}
                          mode="resume"
                          onUpload={(res) => {
                            if (
                              res?.success &&
                              res?.documentUrl &&
                              res?.title
                            ) {
                              setPresentations([
                                { url: res.documentUrl, title: res.title },
                              ]);
                            } else {
                              toast.error("Upload failed");
                            }
                          }}
                          defaultLabel="Upload Resume"
                        />

                        {presentations.length > 0 && (
                          <div className="w-full space-y-2 mt-3">
                            {presentations.map((item, index) => (
                              <div
                                key={index}
                                className="relative bg-white p-2 rounded-md shadow-sm border border-gray-200 w-full text-xs"
                              >
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-green">
                                    Presentation {index + 1}:
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleDeletePresentation(index)
                                    }
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline text-blue-700 hover:text-blue-800 break-all"
                                >
                                  {item.title}
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="col-xl-12">
                        <div className="it-instructor-wrap">
                          <div className="it-signup-agree">
                            <div className="form-check d-flex">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                name="declarationConfirmed"
                                checked={formData.declarationConfirmed}
                                onChange={handleChange}
                              />
                              <label className="form-check-label">
                                I confirm that the information provided is true
                                and complete to the best of my knowledge
                              </label>
                            </div>
                            {errors.declarationConfirmed && (
                              <div className="error-message">
                                {errors.declarationConfirmed}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="it-student-regiform-btn">
                        <button
                          className="ed-btn-theme mr-20 mb-10"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          <span>
                            {isSubmitting ? "Submitting..." : "Submit now"}
                            <i>
                              <RightArrow />
                            </i>
                          </span>
                        </button>

                        <Link className="ed-btn-theme orange" to="/jobs">
                          Cancel
                          <i>
                            <RightArrow />
                          </i>
                        </Link>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default JobApplyMain;
